import _ from 'lodash';
import { emojRanges } from '@/packages/field/types';
import type { PickerColumn, PickerOption, PickerConfirmEventParams } from '@hb/coffee';
import type { AddressForm, AreaJsonData } from '@/shared/utils/areaPicker/types';
import { Validator } from '@hb/finance-ui';

/** 过滤emoj等特殊符号 */
export const emojFilter = (value: string): string => {
  // 去掉emoj
  if (new RegExp(emojRanges.join('|'), 'g').test(String(value))) {
    return _.isString(value)
      ? value.replace(new RegExp(emojRanges.join('|'), 'g'), '')
      : value;
  }

  return value;
};

/**
 * 按3-4-4规则格式化字符串
 * @param input 原始字符串
 * @returns 格式化后的字符串（用空格分隔）
 */
export const formatPhone344 = (input: string): string => {
  // 移除所有空格后处理
  const cleaned = input.replace(/\s/g, '');
  if (!cleaned) return '';

  const segments: string[] = [];
  let cursor = 0;

  // 处理首段（3位）
  if (cleaned.length >= 3) {
    segments.push(cleaned.substr(0, 3));
    cursor = 3;
  } else {
    return cleaned; // 不足3位直接返回
  }

  // 处理后续段（每段4位）
  while (cursor < cleaned.length) {
    const segment = cleaned.substr(cursor, 4);
    segments.push(segment);
    cursor += 4;
  }

  return segments.join(' ');
};


/**
 * 清理手机号：移除国际区号、格式字符等，只保留纯数字
 * 针对手机号码，座机号码直接返回原值
 */
export const cleanPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  let cleaned = phoneNumber.replace(/[\s\-()]/g, '');

  // 移除国际区号前缀：+86、0086、86
  cleaned = cleaned.replace(/^(\+86|0086|86)/, '');

  cleaned = cleaned.replace(/\D/g, '');

  if (Validator.isPhoneNo(cleaned)) {
    return formatPhone344(cleaned);
  }

  return cleaned;
};
/**
 * 处理省市区数据，将平铺数据转为级联数据
 */
export const transformTileDataToCascade = (original: AreaJsonData): PickerColumn => {
  // eslint-disable-next-line camelcase
  const { province_list, city_list, county_list } = original;

  const result: PickerColumn = [];

  // 处理省份
  Object.entries(province_list).forEach(([pCode, pName]) => {
    const provinceNode: PickerOption = {
      text: pName,
      value: pCode,
      children: [],
    };

    // 处理当前省份下的城市
    Object.entries(city_list)
      .filter(([cCode]) => cCode.startsWith(pCode.substring(0, 2)))
      .forEach(([cCode, cName]) => {
        const cityNode: PickerOption = {
          text: cName,
          value: cCode,
          children: [],
        };

        // 处理当前城市下的区县
        Object.entries(county_list)
          .filter(([ctCode]) => ctCode.startsWith(cCode.substring(0, 4)))
          .forEach(([ctCode, ctName]) => {
            if (cityNode.children) {
              cityNode.children.push({
                text: ctName,
                value: ctCode,
              });
            }
          });

        // 移除空的children属性
        if (provinceNode.children) {
          provinceNode.children.push(cityNode);
        }
      });

    result.push(provinceNode);
  });

  return result;
};

/** 将地址表单数据转为级联选择器选择数据 */
export const transformFormDataToPicker = (value: AddressForm | null): PickerColumn => {
  if (!value) {
    return [];
  }

  const {
    province, provinceCode,
    city, cityCode,
    district, districtCode,
  } = value;

  if (!provinceCode && !cityCode && !districtCode) {
    return [];
  }

  return [
    {
      text: province,
      value: provinceCode,
    },
    {
      text: city,
      value: cityCode,
    },
    {
      text: district,
      value: districtCode,
    },
  ];
};

/** 将级联选择器选择数据转为地址表单数据 */
export const transformPickerParamsToForm = (params: PickerConfirmEventParams): AddressForm => {
  const [province, city, district] = params.selectedOptions;

  return {
    provinceCode: province.value as string,
    province: province.text as string,
    cityCode: city.value as string,
    city: city.text as string,
    districtCode: district.value as string,
    district: district.text as string,
  };
};
/** 判断FieldValue值是否有效：若是undefined或null，则认为无效 */
export const isEmptyFieldValue = (value: undefined | number | null | string | unknown):
  boolean => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/** 判断地址值是否有效 */
export const isEmptyAddressValue = (value: unknown):
  boolean => isEmptyFieldValue(value) || (typeof value === 'object' && value !== null && Object.keys(value).length === 0);
